import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES } from "../constants/images";
import { SectionHead, Reveal } from "./Reveal";

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [v, setV] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const t0 = performance.now();
    const dur = 1500;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  return <span ref={ref}>{v}</span>;
};

const Craftsmanship = () => {
  const { t } = useLang();

  return (
    <section id="onama" className="relative bg-[#0D0F12] py-20 sm:py-28 overflow-hidden" data-testid="about-section">
      <div className="tech-grid absolute inset-0 opacity-40" />
      <div className="grain" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <SectionHead eyebrow={t.about.eyebrow} title={t.about.title} dark />

          <Reveal delay={0.2}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F4F1EA]/80">{t.about.p1}</p>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#F4F1EA]/80">{t.about.p2}</p>
          </Reveal>

          <Reveal delay={0.34}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 border border-white/15 divide-x divide-white/15" data-testid="about-stats">
              {t.about.stats.map((s, i) => (
                <div key={i} className="px-4 py-5 text-center">
                  <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#D96B27]">{s.v}</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] mt-1.5">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <h3 className="mt-12 font-mono2 text-xs uppercase tracking-[0.3em] text-[#D96B27]">{t.about.processTitle}</h3>
            <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-6">
              {t.about.process.map((p, i) => (
                <div key={i} className="flex gap-4" data-testid={`process-step-${i + 1}`}>
                  <span className="font-display font-extrabold text-2xl text-[#0D0F12] bg-[#D96B27] w-11 h-11 shrink-0 flex items-center justify-center">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-display font-bold text-[#F4F1EA]">{p.t}</p>
                    <p className="text-sm text-[#94A3B8] mt-1 leading-relaxed">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="relative">
          <Reveal delay={0.15}>
            <div className="relative">
              <motion.div
                initial={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
                whileInView={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
                viewport={{ once: true, amount: 0.2 }}
                className="clip-tg relative overflow-hidden"
              >
                <img src={IMAGES.aboutMain} alt="BoomGradnja master at work" className="w-full h-[420px] sm:h-[520px] object-cover" />
                <div className="absolute inset-0 bg-[#D96B27]/10 mix-blend-multiply" />
              </motion.div>

              <div className="absolute -bottom-10 -start-6 sm:-start-10 w-40 sm:w-56 border-4 border-[#0D0F12] shadow-2xl clip-tg hidden sm:block">
                <img src={IMAGES.aboutSecond} alt="Concrete work" className="w-full h-40 sm:h-52 object-cover" />
              </div>

              <div className="absolute -top-6 -end-4 sm:-end-6 bg-[#D96B27] text-[#0D0F12] px-5 py-4 clip-tg" data-testid="about-big-years">
                <p className="font-display font-extrabold text-3xl sm:text-4xl leading-none">
                  <Counter to={35} />+
                </p>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] mt-1">Godina</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Craftsmanship;
